/**
 * BoardView — the red-string investigation board. ReactFlow canvas with
 * the focused entity pinned at center and documented connections strung
 * around it. Click a node to refocus; click a string to see the evidence.
 * Readability at scale: only the focus neighborhood is ever drawn.
 */
import React, { useMemo, useCallback } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { User, Building2, Crosshair } from "lucide-react";
import { KIND_META, groupOf, portraitSrc } from "./explorerApi";

const KIND_ICON = { person: User, organization: Building2, operation: Crosshair };

const EntityNode = ({ data }) => {
  const g = data.kind_group;
  const Icon = KIND_ICON[g] || Building2;
  const color = KIND_META[g]?.color || "#A68F5E";
  return (
    <div className={`px-2.5 py-2 border flex items-center gap-2 transition-shadow ${data.isFocus ? "shadow-[0_0_24px_rgba(217,45,32,0.35)]" : ""}`}
         style={{ borderColor: data.isFocus ? "#D92D20" : "#3A3630",
                  background: "#141210", width: data.isFocus ? 190 : 150,
                  transform: `rotate(${data.tilt}deg)` }}
         data-testid={`board-node-${data.slug}`}>
      <Handle type="target" position={Position.Top} className="!opacity-0 !w-1 !h-1 !border-0" />
      <span className="shrink-0 flex items-center justify-center overflow-hidden"
            style={{ width: data.isFocus ? 34 : 26, height: data.isFocus ? 42 : 32,
                     border: "1px solid #221F1C", background: "#0B0A0D" }}>
        {data.portrait_url ? (
          <img src={portraitSrc(data.portrait_url)} alt={data.name}
               className="max-w-full max-h-full object-contain" style={{ filter: "sepia(0.2)" }} />
        ) : <Icon size={11} className="text-[#454038]" />}
      </span>
      <span className="min-w-0">
        <span className={`block text-[#E4DFD3] leading-tight truncate ${data.isFocus ? "text-[12px]" : "text-[10.5px]"}`}>
          {data.name}
        </span>
        <span className="block font-mono text-[7px] uppercase tracking-[0.15em] mt-0.5"
              style={{ color }}>
          {KIND_META[g]?.label}
        </span>
      </span>
      <Handle type="source" position={Position.Bottom} className="!opacity-0 !w-1 !h-1 !border-0" />
    </div>
  );
};

const nodeTypes = { entity: EntityNode };

export const BoardView = ({ graph, onFocus, onSelectEdge }) => {
  const { nodes, edges } = useMemo(() => {
    const focus = graph.focus;
    const others = graph.nodes.filter((n) => n.slug !== focus);
    const R = Math.max(300, others.length * 26);
    const cx = 480, cy = 320;

    const fnodes = graph.nodes.map((n) => {
      const isFocus = n.slug === focus;
      let x = cx, y = cy;
      if (!isFocus) {
        const i = others.findIndex((o) => o.slug === n.slug);
        const angle = (2 * Math.PI * i) / Math.max(others.length, 1) - Math.PI / 2;
        x = cx + R * Math.cos(angle);
        y = cy + R * 0.62 * Math.sin(angle);
      }
      // deterministic paper tilt per slug
      const tilt = ((n.slug.charCodeAt(0) + n.slug.length) % 5) - 2;
      return {
        id: n.slug, type: "entity", position: { x, y },
        data: { ...n, isFocus, tilt },
        draggable: true, zIndex: isFocus ? 10 : 5,
      };
    });

    const fedges = graph.edges.map((e) => ({
      id: `${e.a}~${e.b}`, source: e.a, target: e.b, type: "straight",
      data: e,
      style: {
        stroke: "#D92D20",
        strokeWidth: e.strength === "corroborated" ? 1.8 : 1,
        opacity: (e.a === focus || e.b === focus) ? 0.85 : 0.28,
      },
      label: `${e.co_count}`,
      labelStyle: { fill: "#A68F5E", fontSize: 8, fontFamily: "monospace" },
      labelBgStyle: { fill: "#0a0907", opacity: 0.85 },
    }));
    return { nodes: fnodes, edges: fedges };
  }, [graph]);

  const onNodeClick = useCallback((_, n) => {
    if (n.id !== graph.focus) onFocus(n.id);
  }, [graph.focus, onFocus]);

  const onEdgeClick = useCallback((_, e) => {
    onSelectEdge?.({ a: e.data.a, b: e.data.b });
  }, [onSelectEdge]);

  return (
    <div className="h-[62vh] min-h-[420px] border relative"
         style={{ borderColor: "#3A3630", background: "#0B0A08" }}
         data-testid="board-view">
      <ReactFlow
        nodes={nodes} edges={edges} nodeTypes={nodeTypes}
        onNodeClick={onNodeClick} onEdgeClick={onEdgeClick}
        fitView fitViewOptions={{ padding: 0.18 }}
        minZoom={0.25} maxZoom={2}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false} elementsSelectable>
        <Background color="#1d1a16" gap={26} size={1} />
        <Controls showInteractive={false}
                  className="!bg-[#141210] !border-[#3A3630] [&>button]:!bg-[#141210] [&>button]:!border-[#3A3630] [&>button>svg]:!fill-[#A68F5E]" />
      </ReactFlow>
      <div className="absolute bottom-2 left-2 font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C] pointer-events-none">
        click a card to refocus · click a string for evidence
      </div>
    </div>
  );
};
